import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Dialog, Icon, Switch } from '@/ds';
import { EmptyState } from '@/components/EmptyState';
import { Money } from '@/components/money';
import { useOperation, useServices, type ServiceSummary } from '@/api/hooks';
import { useIsMobile } from '@/lib/useMediaQuery';
import { BILLING_LABELS, PROVIDER_LABELS } from '../FinanceScreen';
import { ServiceFormDialog } from '../services/ServiceDialogs';
import { CredentialDialog } from '../services/CredentialDialog';

export function ServicesTab() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const services = useServices();
  const toggle = useOperation<{ id: string; isActive: boolean }>('service.update');
  const move = useOperation<{ id: string; sortOrder: number }>('service.update');
  const remove = useOperation<{ id: string }>('service.delete');

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ServiceSummary | null>(null);
  const [deleting, setDeleting] = useState<ServiceSummary | null>(null);
  const [credentialFor, setCredentialFor] = useState<ServiceSummary | null>(null);

  const rows = services.data?.services ?? [];

  function swap(index: number, direction: -1 | 1) {
    const current = rows[index];
    const neighbour = rows[index + direction];
    if (!current || !neighbour) return;
    move.mutate({ id: current.id, sortOrder: neighbour.sortOrder });
    move.mutate({ id: neighbour.id, sortOrder: current.sortOrder });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <p
          style={{
            font: '400 13px/1.5 var(--font-text)',
            color: 'var(--text-muted)',
            maxWidth: 560,
          }}
        >
          Порядок задаёт положение на дашборде. Отключённый сервис не попадает в общие суммы, но
          остаётся в истории. Ключ хранится зашифрованным, в интерфейсе видны только последние четыре
          символа. Новые типы интеграций добавляются кодом, а не через этот экран.
        </p>
        <Button
          variant="secondary"
          onClick={() => setCreating(true)}
          iconLeft={<Icon name="Plus" size={16} />}
        >
          Создать сервис
        </Button>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon="Boxes"
          title="Сервисов пока нет"
          description="Заведите сервис: название, тип биллинга, валюта и кошелёк по умолчанию. Пополнения и расход вносятся руками."
        />
      ) : (
        <Card padding={isMobile ? 'sm' : 'md'}>
          <div className="kj-scroll-x">
            <table className="kj-table">
              <thead>
                <tr>
                  <th>Сервис</th>
                  <th>Биллинг</th>
                  <th>Кошелёк</th>
                  <th>Ключ</th>
                  <th>Баланс</th>
                  <th>Активен</th>
                  <th>Порядок</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((service, index) => (
                  <tr key={service.id}>
                    <td data-label="Сервис" style={{ color: 'var(--text-strong)', fontWeight: 500 }}>
                      {service.name}
                      {service.isActive ? null : (
                        <span style={{ marginLeft: 8 }}>
                          <Badge tone="neutral">Отключён</Badge>
                        </span>
                      )}
                      <div style={{ font: '400 12px var(--font-text)', color: 'var(--text-subtle)' }}>
                        {PROVIDER_LABELS[service.provider] ?? service.provider}
                      </div>
                    </td>
                    <td data-label="Биллинг" style={{ color: 'var(--text-muted)' }}>
                      {BILLING_LABELS[service.billingModel] ?? service.billingModel}
                    </td>
                    <td data-label="Кошелёк" style={{ color: 'var(--text-muted)' }}>
                      {service.defaultWalletName ?? '—'}
                    </td>
                    <td data-label="Ключ">
                      {!service.needsCredential ? (
                        <span style={{ color: 'var(--text-subtle)' }}>не нужен</span>
                      ) : service.credential ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span
                            style={{
                              fontFamily: 'var(--font-mono)',
                              fontSize: 12,
                              color: 'var(--text-muted)',
                            }}
                          >
                            {service.credential.maskedHint}
                          </span>
                          {service.credential.isValid === false ? (
                            <Badge tone="danger" dot>
                              не принят
                            </Badge>
                          ) : null}
                        </span>
                      ) : (
                        <Badge tone="warning">не задан</Badge>
                      )}
                    </td>
                    <td data-label="Баланс">
                      <Money
                        minor={service.balanceMinor}
                        currency={service.currency}
                        size="sm"
                        tone={(service.balanceMinor ?? 0) < 0 ? 'danger' : 'normal'}
                      />
                    </td>
                    <td data-label="Активен">
                      <Switch
                        checked={service.isActive}
                        onChange={(e) =>
                          toggle.mutate({ id: service.id, isActive: e.target.checked })
                        }
                      />
                    </td>
                    <td data-label="Порядок" style={{ whiteSpace: 'nowrap' }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={index === 0}
                        onClick={() => swap(index, -1)}
                      >
                        Выше
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={index === rows.length - 1}
                        onClick={() => swap(index, 1)}
                      >
                        Ниже
                      </Button>
                    </td>
                    <td data-label="" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/finance/${service.id}`)}
                      >
                        Открыть
                      </Button>
                      {service.needsCredential ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCredentialFor(service)}
                        >
                          Ключ
                        </Button>
                      ) : null}
                      <Button variant="ghost" size="sm" onClick={() => setEditing(service)}>
                        Изменить
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleting(service)}>
                        Удалить
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ServiceFormDialog open={creating} onClose={() => setCreating(false)} />
      {editing ? (
        <ServiceFormDialog service={editing} open onClose={() => setEditing(null)} />
      ) : null}
      {credentialFor ? (
        <CredentialDialog service={credentialFor} open onClose={() => setCredentialFor(null)} />
      ) : null}

      <Dialog
        open={deleting !== null}
        title="Удалить сервис"
        description={
          deleting
            ? `«${deleting.name}» пропадёт из списков. Пополнения, расход и сверки останутся в базе, общие суммы за прошлые периоды не изменятся.`
            : ''
        }
        onClose={() => setDeleting(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => {
                if (!deleting) return;
                remove.mutate({ id: deleting.id }, { onSuccess: () => setDeleting(null) });
              }}
            >
              Удалить
            </Button>
          </>
        }
      />
    </div>
  );
}
